"use client";

import { env } from "@/env.mjs";
import { readUserErrorI18n } from "@v1/backend/logic/errors/user_error";
import { fetchTurnstileToken } from "./turnstile-client";

export type VisitorGrantTarget =
  | { readonly surface: "help_center"; readonly orgSlug: string }
  | { readonly surface: "website_embed"; readonly siteKey: string; readonly handshakeToken: string | null };

export type VisitorGrantFailure = "too_many" | "bot_check_failed" | "unavailable";

export type VisitorGrantResult =
  | { readonly ok: true; readonly visitorToken: string; readonly threadId: string }
  | { readonly ok: false; readonly code: VisitorGrantFailure };

const SESSION_PATH = "/customer-chat/visitor/session";

function failureCode(raw: unknown): VisitorGrantFailure {
  return raw === "too_many" || raw === "bot_check_failed" ? raw : "unavailable";
}

/** Cloudflare tokens live 300 s; a prefetched one older than this is dropped. */
const PREFETCH_MAX_AGE_MS = 240_000;

export interface TurnstilePrefetch {
  /** Hands the prefetched token over once, then fetches fresh ones. */
  readonly take: () => Promise<string | null>;
  /** Chat closed: drops the pending check and its widget. */
  readonly cancel: () => void;
}

/**
 * Starts the bot check early (window opened). `host` = where a checkbox may appear;
 * `onInteractive(true)` while it waits for the visitor's click.
 */
export function prefetchTurnstileToken(host?: HTMLElement, onInteractive?: (waiting: boolean) => void): TurnstilePrefetch {
  const startedAt = Date.now();
  const controller = new AbortController();
  const fresh = (): Promise<string | null> => fetchTurnstileToken(host, controller.signal, onInteractive);
  let pending: Promise<string | null> | null = fresh();
  return {
    take: () => {
      const prefetched = pending;
      pending = null;
      if (prefetched === null || Date.now() - startedAt > PREFETCH_MAX_AGE_MS) return fresh();
      // Early check failed (network blip, checkbox ignored): one fresh try on send.
      return prefetched.then((token) => token ?? fresh());
    },
    cancel: () => controller.abort(),
  };
}

/** Invisible bot check, then the server opens or renews the visitor's chat session. */
export async function requestVisitorGrant(
  target: VisitorGrantTarget,
  visitorToken: string | null,
  takeToken: () => Promise<string | null> = fetchTurnstileToken,
): Promise<VisitorGrantResult> {
  const turnstileToken = await takeToken();
  if (turnstileToken === null) return { ok: false, code: "bot_check_failed" };
  const body =
    target.surface === "help_center"
      ? { surface: target.surface, org_slug: target.orgSlug }
      : { surface: target.surface, site_key: target.siteKey, handshake_token: target.handshakeToken ?? undefined };
  try {
    const response = await fetch(`${env.NEXT_PUBLIC_CONVEX_SITE_URL.replace(/\/$/, "")}${SESSION_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...body, visitor_token: visitorToken ?? undefined, turnstile_token: turnstileToken }),
    });
    const result: unknown = await response.json();
    if (typeof result !== "object" || result === null) return { ok: false, code: "unavailable" };
    const record = result as Record<string, unknown>;
    if (record.ok === true && typeof record.visitor_token === "string" && typeof record.thread_id === "string") {
      return { ok: true, visitorToken: record.visitor_token, threadId: record.thread_id };
    }
    return { ok: false, code: failureCode(record.code) };
  } catch {
    return { ok: false, code: "unavailable" };
  }
}

/** The server asks for a fresh bot check (grant expired or used up). */
export function isVisitorVerificationRequired(error: unknown): boolean {
  const data = error !== null && typeof error === "object" && "data" in error ? (error as { data?: unknown }).data : undefined;
  return readUserErrorI18n(data)?.key === "errors.customerChat.verificationRequired";
}

/** Message key (namespace `messenger.botCheck`) for a failed grant. */
export function visitorGrantFailureKey(code: VisitorGrantFailure): "zuViele" | "nichtGeprueft" | "nichtErreichbar" {
  if (code === "too_many") return "zuViele";
  if (code === "bot_check_failed") return "nichtGeprueft";
  return "nichtErreichbar";
}
